import React, { useEffect, useState } from "react";
import useWifi, { HOTSPOT, NETWORKS, wifi } from "../hooks/useWifi";
import { notify } from "./Notification";
import { ReactComponent as WifiArcs } from "../assets/svgs/sf-symbols/wifi/wifi.svg";
import { ReactComponent as WifiOff } from "../assets/svgs/sf-symbols/wifi/wifi.slash.svg";
import { ReactComponent as Lock } from "../assets/svgs/sf-symbols/wifi/lock.fill.svg";
import { ReactComponent as Hotspot } from "../assets/svgs/sf-symbols/wifi/personalhotspot.svg";
import { ReactComponent as Chevron } from "../assets/svgs/sf-symbols/wifi/chevron.right.svg";
import { ReactComponent as HotspotBattery } from "../assets/svgs/sf-symbols/battery/battery.75percent.svg";

// Wi-Fi glyph whose arcs show signal strength (1-3), and ripple outward while connecting.
// On a Personal Hotspot, macOS shows the hotspot's link symbol instead.
export const WifiGlyph = ({ on = true, connecting, hotspot, signal = 3, className = "" }) =>
  on && hotspot && !connecting ? (
    <Hotspot className={className} />
  ) : on ? (
    <WifiArcs className={`wifi-glyph signal-${signal} ${connecting ? "is-connecting" : ""} ${className}`} />
  ) : (
    <WifiOff className={className} />
  );

const NetworkRow = ({ network, current, connecting, onJoin }) => {
  const isCurrent = network.name === current;
  const isConnecting = network.name === connecting;
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={isCurrent}
      onClick={() => onJoin(network)}
      className="menu-option"
    >
      <span className={`menu-option-icon ${isCurrent ? "is-selected" : ""}`}>
        <WifiGlyph signal={network.signal} connecting={isConnecting} className="wifi-row-glyph" />
      </span>
      <span className="flex-1 truncate">{network.name}</span>
      {network.secure && <Lock className="menu-accessory-lock" aria-label="Secured" />}
    </button>
  );
};

// The Wi-Fi menu from the menu bar, laid out like macOS Tahoe's.
const WifiMenu = ({ open, onClose, onOpenSettings }) => {
  const { on, current, connecting, known } = useWifi();
  const [showOthers, setShowOthers] = useState(false);

  // Each time it opens, Other Networks starts folded, as in macOS.
  useEffect(() => {
    if (!open) setShowOthers(false);
  }, [open]);

  // Like macOS, choosing a network closes the menu; the menu bar icon then shows it connecting.
  const join = (network) => {
    onClose();
    if (network.secure && !known.includes(network.name)) {
      notify(`"${network.name}" needs a password, and this Mac doesn't know it. The ones above work.`);
      return;
    }
    wifi.join(network.name);
  };

  const knownNetworks = NETWORKS.filter((n) => known.includes(n.name));
  const otherNetworks = NETWORKS.filter((n) => !known.includes(n.name));
  const rowProps = { current, connecting, onJoin: join };

  return (
    <div className="menu-body" role="menu" aria-label="Wi-Fi">
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={on}
        onClick={wifi.toggle}
        className="menu-head menu-head--toggle"
      >
        <span>Wi-Fi</span>
        <span className={`menu-switch ${on ? "is-on" : ""}`} aria-hidden="true">
          <span />
        </span>
      </button>

      {on && (
        <>
          <hr className="menu-sep" />
          <p className="menu-section">Personal Hotspot</p>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={current === HOTSPOT.name}
            onClick={() => join({ name: HOTSPOT.name, secure: false })}
            className="menu-option"
          >
            <span className={`menu-option-icon ${current === HOTSPOT.name ? "is-selected" : ""}`}>
              <Hotspot className={`hotspot-glyph ${connecting === HOTSPOT.name ? "is-pulsing" : ""}`} />
            </span>
            <span className="flex-1 truncate">{HOTSPOT.name}</span>
            <span className="menu-accessory">{HOTSPOT.cellular}</span>
            <HotspotBattery className="menu-accessory-battery" aria-label={`${HOTSPOT.battery * 100}% battery`} />
          </button>

          <hr className="menu-sep" />
          <p className="menu-section">{knownNetworks.length > 1 ? "Known Networks" : "Known Network"}</p>
          {knownNetworks.map((n) => (
            <NetworkRow key={n.name} network={n} {...rowProps} />
          ))}

          <button
            type="button"
            aria-expanded={showOthers}
            onClick={() => setShowOthers(!showOthers)}
            className="menu-disclosure"
          >
            <span>Other Networks</span>
            <Chevron className={`menu-disclosure-chevron ${showOthers ? "is-open" : ""}`} aria-hidden="true" />
          </button>
          {showOthers &&
            (otherNetworks.length ? (
              otherNetworks.map((n) => <NetworkRow key={n.name} network={n} {...rowProps} />)
            ) : (
              <p className="menu-info">No Other Networks</p>
            ))}
        </>
      )}

      <hr className="menu-sep" />
      <button type="button" role="menuitem" onClick={onOpenSettings} className="menu-item">
        Wi-Fi Settings…
      </button>
    </div>
  );
};

export default WifiMenu;
